import {
  useCreatePresetMutationApi,
  useUpdatePresetMutationApi,
} from '@aibees/shared/api';

import {
  AccessSettings,
  DialogActions,
  DialogTitle,
  OutlinedInput,
  usePopover,
} from '@aibees/shared/components';
import {
  CreatePresetRequest,
  PresetVisibility,
} from '@aibees/shared/definition';
import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { OperationMode } from '@aibees/shared/features/template-management-dialog';
import {
  TransferList,
  TransferListConfig,
} from '@aibees/shared/features/transfer-list';
import { useResetTransferList } from '@aibees/shared/features/transfer-list/hooks/use-reset-transfer-list';
import useTransferListStore from '@aibees/shared/features/transfer-list/store/transfer-list-store';
import { useBoolean, useMount, useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Dialog, DialogContent, Fade, Typography } from '@mui/material';
import { format } from 'date-fns';
import { memo, useCallback, useMemo, useState } from 'react';
import { MAX_TEMPLATE_NAME_LENGTH } from '../../constant';
import { useAdvanceTableContext } from '../../context';
import { TableLayoutTemplate } from '../table-layout-modifier-dialog';
import { useTableLayoutTemplateContext } from '../table-layout-modifier-dialog/context';
import { obtainTableModifierDataFromConfig } from '../table-layout-modifier/util';
import * as Styled from './styles';
import * as SX from './sx';
import type { TableLayoutModifierCreateDialogProps as Props } from './types';

export const TableLayoutModifierCreateDialog = memo(
  ({ className, state, config: createDialogConfig }: Props) => {
    const notify = useNotify();
    const { config } = useAdvanceTableContext();
    const {
      templateName,
      setTemplateName,
      setOperationMode,
      setTemplateEditItem,
      isPrivate,
      setId,
      setNewlyCreatedId,
      tableLayoutModifierDialogState,
      templateEditItem,
      operationMode,
      id,
    } = useTableLayoutTemplateContext();
    const reset = useResetTransferList();
    const store = useTransferListStore();
    const editApi = useUpdatePresetMutationApi(templateEditItem?.id ?? '');
    const [nameError, setNameError] = useState('');
    const transferListConfig = obtainTableModifierDataFromConfig(
      config,
      createDialogConfig?.groupedItems
    );
    const isEdit = useMemo(() => {
      return operationMode === OperationMode.EDIT;
    }, [operationMode]);
    const dialogTitle = useMemo(
      () => (isEdit ? 'Edit Template Name' : 'Create New Template'),
      [isEdit]
    );
    const submitLoading = useBoolean();
    const createApi = useCreatePresetMutationApi<TableLayoutTemplate>(false);
    const api = isEdit ? editApi : createApi;
    const transferListState = usePopover(false);
    const [visibility, setVisibility] = useState<PresetVisibility>(
      PresetVisibility.INTERNAL
    );

    const handleClose = () => {
      tableLayoutModifierDialogState?.onTrue();
      state.onFalse();
      setTemplateName('');
      setOperationMode(OperationMode.CREATE);
      setTemplateEditItem(undefined);
      reset();
      setNameError('');
    };

    const handleContinue = () => {
      transferListState.onOpen(document.body as HTMLElement);
      state.onFalse();
      if (!isEdit) {
        reset();
      }
    };

    const handleChangeVisibility = (e: PresetVisibility) => {
      setVisibility(e);
    };

    const handleChangeName = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setTemplateName(value);
        if (value.length > MAX_TEMPLATE_NAME_LENGTH) {
          setNameError(
            `Template name must be ${MAX_TEMPLATE_NAME_LENGTH} characters or fewer.`
          );
        } else if (!value.trim()) {
          setNameError('Template name is required');
        } else {
          setNameError('');
        }
      },
      [setTemplateName]
    );
    const handleCreateTemplate = (
      newIncluded?: string[],
      newExcluded?: string[]
    ) => {
      const key = Date.now().toString();
      submitLoading.onTrue();

      const dataDraft = {
        name: templateName,
        type: config.columnModifierPresetType!,
        value: {
          included: newIncluded,
          excluded: newExcluded,
          private: isPrivate.value,
          columns: store.config?.items,
          default: false,
        },
        visibility: isEdit ? templateEditItem?.visibility : visibility,
      } as CreatePresetRequest<TableLayoutTemplate>;

      api
        .mutateAsync(dataDraft)
        .then((response) => {
          submitLoading.onFalse();
          handlePrevious();
          setTemplateName('');
          notify.complete(
            !isEdit ? 'Created successfully.' : 'Column Template Updated',
            !isEdit
              ? 'Template has been created successfully.'
              : 'Your changes have been saved and applied to this template.',
            key,
            {
              icon: notificationIconMap.TableLayoutModifier!,
            }
          );

          tableLayoutModifierDialogState?.onTrue();
          state.onFalse();
          transferListState.onClose();

          setId(response.id);
          setNewlyCreatedId(response.id);
          setVisibility(PresetVisibility.INTERNAL);
        })
        .catch((err) => notify.error(err));
    };

    const handlePrevious = () => {
      tableLayoutModifierDialogState?.onTrue();
      state.onFalse();
      transferListState.onClose();
      setOperationMode(OperationMode.CREATE);
      setTemplateName('');
      setTemplateEditItem(undefined);
      reset();
    };

    useMount(() => {
      store.setConfig(transferListConfig);
    });

    return (
      <Styled.Wrapper
        className={classnames('TableLayoutModifierCreateDialog', className)}
        sx={SX.root}
      >
        <Dialog
          open={state.value}
          fullWidth
          maxWidth="xs"
          TransitionComponent={Fade}
          onClose={handleClose}
        >
          <DialogTitle
            title={
              <Typography color="gray.800" fontWeight={400} variant="body2">
                {dialogTitle}
              </Typography>
            }
            closeIconSize={16}
            closeIconColor="grey.700"
            iconColor="grey.700"
            iconSize={16}
            onClose={handleClose}
          />

          <DialogContent sx={SX.dialogContent}>
            <OutlinedInput
              label="Template Name:"
              size="small"
              name="name"
              fullWidth
              placeholder={`${createDialogConfig?.label} Template- ${format(
                new Date(),
                'MMM dd'
              )}`}
              required
              inputProps={{ maxLength: MAX_TEMPLATE_NAME_LENGTH + 1 }}
              error={!!nameError}
              helperText={nameError}
              value={templateName}
              onChange={handleChangeName}
            />

            <AccessSettings
              onSubmit={handleChangeVisibility}
              item={templateEditItem}
            />
          </DialogContent>

          <DialogActions
            submitTitle="Continue"
            disabled={!templateName || !!nameError}
            onClose={handleClose}
            onSubmit={handleContinue}
          />
        </Dialog>

        <TransferList
          title={`Edit Columns ${createDialogConfig?.label} Search`}
          includedTitle={`Columns Set for ${createDialogConfig?.label} Search`}
          excludedTitle={`Do Not ${createDialogConfig?.label} Search Columns`}
          tooltipTitle="All Columns Included"
          icon="view-week-outline"
          saveButtonProps={{
            children: 'Save Layout',
          }}
          dialogMode
          id={id}
          config={store.config as TransferListConfig}
          state={transferListState}
          onSubmit={handleCreateTemplate}
        />
      </Styled.Wrapper>
    );
  }
);

TableLayoutModifierCreateDialog.displayName = 'TableLayoutModifierCreateDialog';
